package com.sweetlime.dungeonamigo.presentation.features.profile

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.sweetlime.dungeonamigo.model.Character
import com.sweetlime.dungeonamigo.model.UserProfile
import com.sweetlime.dungeonamigo.repository.AuthRepository
import com.sweetlime.dungeonamigo.repository.CharacterRepository
import com.sweetlime.dungeonamigo.repository.UserRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

class ProfileViewModel(
    private val userRepo: UserRepository,
    private val characterRepo: CharacterRepository,
    private val authRepo: AuthRepository,
) : ViewModel() {

    private val _userProfile = MutableStateFlow<UserProfile?>(null)
    val userProfile: StateFlow<UserProfile?> = _userProfile.asStateFlow()

    private val _characters = MutableStateFlow<List<Character>>(emptyList())
    val characters: StateFlow<List<Character>> = _characters.asStateFlow()

    val favoriteClass: StateFlow<String> = _characters
        .map { chars ->
            chars.filter { it.characterClass.isNotBlank() }
                .groupBy { it.characterClass }
                .maxByOrNull { it.value.size }
                ?.key ?: "—"
        }
        .stateIn(viewModelScope, SharingStarted.Eagerly, "—")

    private val _signedOut = MutableStateFlow(false)
    val signedOut: StateFlow<Boolean> = _signedOut.asStateFlow()

    init {
        val userId = authRepo.currentUserId()
        if (userId != null) {
            viewModelScope.launch {
                _userProfile.value = userRepo.getUserProfile(userId)
            }
            viewModelScope.launch {
                characterRepo.getCharacters(userId)
                    .catch { /* ignore */ }
                    .collect { chars -> _characters.value = chars }
            }
        }
    }

    fun refreshProfile() {
        val userId = authRepo.currentUserId() ?: return
        viewModelScope.launch {
            _userProfile.value = userRepo.getUserProfile(userId)
        }
    }

    fun updateUsername(newName: String) {
        if (newName.isBlank()) return
        val userId = authRepo.currentUserId() ?: return
        viewModelScope.launch {
            runCatching {
                userRepo.updateUsername(userId, newName)
                _userProfile.value = _userProfile.value?.copy(username = newName)
            }
        }
    }

    fun updateAvatarId(avatarId: String) {
        val userId = authRepo.currentUserId() ?: return
        viewModelScope.launch {
            runCatching {
                userRepo.updateAvatarId(userId, avatarId)
                _userProfile.value = _userProfile.value?.copy(avatarId = avatarId)
            }
        }
    }

    fun updatePortraitColors(primary: Long, secondary: Long) {
        val userId = authRepo.currentUserId() ?: return
        viewModelScope.launch {
            runCatching {
                userRepo.updatePortraitColors(userId, primary, secondary)
                _userProfile.value = _userProfile.value?.copy(
                    portraitPrimaryColor = primary,
                    portraitSecondaryColor = secondary,
                )
            }
        }
    }

    fun signOut() {
        viewModelScope.launch {
            runCatching { authRepo.logout() }
            _signedOut.value = true
        }
    }

    fun deleteAccount() {
        val userId = authRepo.currentUserId() ?: run { _signedOut.value = true; return }
        viewModelScope.launch {
            runCatching {
                userRepo.deleteUserData(userId)
                authRepo.deleteAccount()
            }
            _signedOut.value = true
        }
    }
}
